import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCities,
  addCity,
  updateCity,
  deleteCity,
} from "../../features/cities/citiesSlice";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

const CityManager = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { cities, loading } = useSelector((state) => state.cities);
  const [showModal, setShowModal] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [formData, setFormData] = useState({
    name_ar: "",
    name_en: "",
    shipping_price: "",
    country_id: 1,
  });

  useEffect(() => {
    dispatch(fetchCities());
  }, [dispatch]);

  const showAlert = (title, icon = "success") => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title,
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  };

  const handleOpen = (city = null) => {
    if (city) {
      setFormData({
        name_ar: city.name_ar,
        name_en: city.name_en,
        shipping_price: city.shipping_price,
        country_id: 1,
      });
      setEditingCity(city);
    } else {
      setFormData({
        name_ar: "",
        name_en: "",
        shipping_price: "",
        country_id: 1,
      });
      setEditingCity(null);
    }
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingCity(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCity) {
      dispatch(updateCity({ id: editingCity.id, data: formData })).then(() => {
        showAlert(t("updated_successfully"));
      });
    } else {
      dispatch(addCity(formData)).then(() => {
        showAlert(t("added_successfully"));
      });
    }
    handleClose();
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t("are_you_sure"),
      text: t("you_wont_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("yes_delete_it"),
      cancelButtonText: t("cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteCity(id)).then(() => {
          showAlert(t("deleted_successfully"));
        });
      }
    });
  };

  return (
    <div className="container mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h3 className="fs-2"> {t("city_management")}</h3>
          <span className="fs-4">({cities.length})</span>
        </div>
        <div>
          <button className="btn btn-success mb-3" onClick={() => handleOpen()}>
            + {t("new_city")}
          </button>
        </div>
      </div>

      {loading ? (
        <p>{t("loading")}...</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped text-center">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>{t("name_ar")}</th>
                <th>{t("name_en")}</th>
                <th>{t("shipping_price")}</th>
                <th>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {cities.map((city, index) => (
                <tr key={city.id}>
                  <td>{index + 1}</td>
                  <td>{city.name_ar}</td>
                  <td>{city.name_en}</td>
                  <td>{city.shipping_price}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-light mx-2"
                      onClick={() => handleOpen(city)}
                    >
                      <i className="fa-solid fa-pen-to-square text-primary"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-light  mx-2"
                      onClick={() => handleDelete(city.id)}
                    >
                      <i className="fa-solid fa-trash-can text-danger"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <form onSubmit={handleSubmit} className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingCity ? t("edit_city") : t("add_city")}
                </h5>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">{t("name_ar")}</label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={formData.name_ar}
                    onChange={(e) =>
                      setFormData({ ...formData, name_ar: e.target.value })
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">{t("name_en")}</label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={formData.name_en}
                    onChange={(e) =>
                      setFormData({ ...formData, name_en: e.target.value })
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">{t("shipping_price")}</label>
                  <input
                    type="number"
                    className="form-control"
                    required
                    value={formData.shipping_price}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        shipping_price: e.target.value,
                      })
                    }
                  />
                </div>

                <input type="hidden" value={formData.country_id} readOnly />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                >
                  {t("cancel")}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t("save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CityManager;
